import { ImageUploadDropzone } from '@/components/shared/ImageUploadDropzone';
import { type Gallery } from '@/config/objects/gallery';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErrorState,
  Header,
  HeaderDescription,
  HeaderTitle,
  Loading,
  useApiClient,
} from '@startupquickstart/frontend';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GripVertical, ImageIcon, Loader2, Plus, Star, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

interface GalleryListProps {
  /**
   * Attraction ID for fetching gallery items
   */
  attractionId: string;
  /**
   * Current attraction's featured image ID
   */
  featuredImageId?: string;
  /**
   * Callback when featured image is swapped
   */
  onFeaturedImageSwap?: () => void;
  /**
   * Custom class name for the container
   */
  className?: string;
}

interface SortableGalleryItemProps {
  item: Gallery;
  isFeatured: boolean;
  isSwapping: boolean;
  onDelete: (id: string) => void;
  onSetFeatured: (galleryItem: Gallery) => void;
}

/**
 * Individual sortable gallery item card
 */
function SortableGalleryItem({
  item,
  isFeatured,
  isSwapping,
  onDelete,
  onSetFeatured,
}: SortableGalleryItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group">
      <Card className="overflow-hidden border-2 hover:border-muted-foreground/30 transition-colors p-0">
        {/* Image */}
        <div className="relative aspect-square bg-muted m-0">
          <img
            key={item.mediaId}
            src={item.media?.url}
            alt={item.media?.name || 'Gallery image'}
            className="w-full h-full object-cover"
          />

          {/* Order Badge */}
          <div className="absolute top-2 right-2 px-2 py-1 bg-black/60 rounded text-white text-xs font-medium z-10">
            #{item.order ?? 0}
          </div>

          {/* Featured Badge */}
          {isFeatured && (
            <div className="absolute bottom-2 left-2 px-2 py-1 bg-yellow-500 rounded text-white text-xs font-medium flex items-center gap-1 z-10">
              <Star className="h-3 w-3 fill-white" />
              Featured
            </div>
          )}

          {/* Actions Overlay */}
          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => onSetFeatured(item)}
              disabled={isFeatured || isSwapping}
              title={isFeatured ? 'Already featured' : 'Set as featured image'}
              className="cursor-pointer"
            >
              {isSwapping ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Star className="h-4 w-4" />
              )}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="destructive"
              onClick={() => onDelete(item.id)}
              title="Delete image"
              className="cursor-pointer"
              disabled={isSwapping}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>

          {/* Drag Handle - Above overlay */}
          <div
            {...attributes}
            {...listeners}
            className="absolute top-2 left-2 p-1.5 bg-black/60 rounded cursor-grab active:cursor-grabbing hover:bg-black/80 transition-colors z-20"
          >
            <GripVertical className="h-4 w-4 text-white" />
          </div>
        </div>

        {/* Unpublished Indicator */}
        {!item.isPublished && (
          <div className="px-3 py-1 bg-muted text-muted-foreground text-xs text-center border-t">
            Unpublished
          </div>
        )}
      </Card>
    </div>
  );
}

/**
 * Gallery grid component with drag-and-drop reordering
 */
export function GalleryList({
  attractionId,
  featuredImageId,
  onFeaturedImageSwap,
  className,
}: GalleryListProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [newMediaId, setNewMediaId] = useState<string | undefined>();
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [swappingItemId, setSwappingItemId] = useState<string | null>(null);
  const [isSwapping, setIsSwapping] = useState(false);

  // Fetch gallery items
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['gallery', attractionId],
    queryFn: async () => {
      const response = await apiClient.get<{ data: Gallery[] }>(
        `/attractions/${attractionId}/gallery`,
        {
          params: { limit: 100, orderBy: { order: 'asc' } },
        },
      );
      return response.data;
    },
    placeholderData: keepPreviousData,
  });

  const galleryItems = data || [];

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Update order mutation
  const updateOrderMutation = useMutation({
    mutationFn: async ({ id, order }: { id: string; order: number }) => {
      return apiClient.put(`/attractions/${attractionId}/gallery/${id}`, { order });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gallery', attractionId] });
    },
  });

  // Add gallery item mutation
  const addMutation = useMutation({
    mutationFn: async (mediaId: string) => {
      const maxOrder = Math.max(...galleryItems.map(item => item.order ?? 0), -1);
      return apiClient.post(`/attractions/${attractionId}/gallery`, {
        mediaId,
        order: maxOrder + 1,
        isPublished: true,
      });
    },
    onSuccess: () => {
      toast.success('Image added to gallery');
      queryClient.invalidateQueries({ queryKey: ['gallery', attractionId] });
      setUploadModalOpen(false);
      setNewMediaId(undefined);
    },
    onError: (error: Error) => {
      toast.error(error?.message || 'Failed to add image');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (galleryId: string) => {
      return apiClient.delete(`/attractions/${attractionId}/gallery/${galleryId}`);
    },
    onSuccess: () => {
      toast.success('Image deleted from gallery');
      queryClient.invalidateQueries({ queryKey: ['gallery', attractionId] });
      setDeleteConfirmId(null);
    },
    onError: (error: Error) => {
      toast.error(error?.message || 'Failed to delete image');
    },
  });

  // Swap featured image mutation
  const swapFeaturedMutation = useMutation({
    mutationFn: async (galleryItem: Gallery) => {
      setSwappingItemId(galleryItem.id);
      setIsSwapping(true);
      const oldFeaturedImageId = featuredImageId;
      const newFeaturedImageId = galleryItem.mediaId;

      // Step 1: Update the attraction's featuredImageId to the gallery item's current media
      await apiClient.put(`/attractions/${attractionId}`, {
        featuredImageId: newFeaturedImageId,
      });

      // Step 2: Update the gallery item's mediaId to the old featured image
      await apiClient.put(`/attractions/${attractionId}/gallery/${galleryItem.id}`, {
        mediaId: oldFeaturedImageId,
      });

      return { oldFeaturedImageId, newFeaturedImageId };
    },
    onSuccess: async () => {
      toast.success('Featured image swapped successfully');
      // Remove cached queries to force fresh fetch
      queryClient.removeQueries({ queryKey: ['gallery', attractionId] });
      // Remove both list and detail queries for the attraction
      queryClient.removeQueries({ queryKey: ['Attraction'] });
      // Wait for refetch to complete
      await refetch();
      onFeaturedImageSwap?.();
      setSwappingItemId(null);
      setIsSwapping(false);
    },
    onError: (error: Error) => {
      toast.error(error?.message || 'Failed to swap featured image');
      setSwappingItemId(null);
      setIsSwapping(false);
    },
  });

  /**
   * Handle drag end - reorder items
   */
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = galleryItems.findIndex(item => item.id === active.id);
      const newIndex = galleryItems.findIndex(item => item.id === over.id);

      const reorderedItems = arrayMove(galleryItems, oldIndex, newIndex);

      // Update orders for all affected items
      const updates = reorderedItems.map((item, index) => ({
        id: item.id,
        order: index,
      }));

      // Optimistically update the UI
      queryClient.setQueryData(['gallery', attractionId], reorderedItems);

      // Send updates to server
      try {
        await Promise.all(updates.map(update => updateOrderMutation.mutateAsync(update)));
        toast.success('Gallery order updated');
      } catch {
        toast.error('Failed to update order');
        refetch(); // Revert on error
      }
    }
  };

  /**
   * Handle upload submit
   */
  const handleUploadSubmit = () => {
    if (newMediaId) {
      addMutation.mutate(newMediaId);
    }
  };

  /**
   * Handle delete with confirmation
   */
  const handleDelete = (id: string) => {
    setDeleteConfirmId(id);
  };

  const confirmDelete = () => {
    if (deleteConfirmId) {
      deleteMutation.mutate(deleteConfirmId);
    }
  };

  /**
   * Handle set as featured
   */
  const handleSetFeatured = (galleryItem: Gallery) => {
    if (!featuredImageId) {
      toast.error('No featured image to swap with');
      return;
    }
    swapFeaturedMutation.mutate(galleryItem);
  };

  // Only show loading on initial load, not during swap
  if (isLoading && !isSwapping && !data) {
    return (
      <div className={className}>
        <div className="p-8">
          <Loading text="Loading gallery..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={className}>
        <div className="p-8">
          <ErrorState title="Failed to load gallery" error={error} />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={className}>
        <div className="space-y-4">
          {/* Header matching CrudListView style */}
          <div className="flex items-end justify-between mb-4">
            <Header>
              <HeaderTitle>Gallery</HeaderTitle>
              <HeaderDescription>
                Manage attraction images ({galleryItems.length}{' '}
                {galleryItems.length === 1 ? 'image' : 'images'})
              </HeaderDescription>
            </Header>
            <Button onClick={() => setUploadModalOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Images
            </Button>
          </div>

          {/* Gallery Grid */}
          {galleryItems.length === 0 ? (
            <Card className="border-dashed">
              <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                <div className="mb-4 text-muted-foreground/50">
                  <ImageIcon className="h-12 w-12" />
                </div>
                <h3 className="text-lg font-semibold mb-2">No images in gallery</h3>
                <p className="text-muted-foreground mb-6 max-w-sm">
                  Add images to showcase this attraction
                </p>
                <Button onClick={() => setUploadModalOpen(true)} size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Images
                </Button>
              </div>
            </Card>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={galleryItems.map(item => item.id)}
                strategy={rectSortingStrategy}
              >
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {galleryItems.map(item => (
                    <SortableGalleryItem
                      key={item.id}
                      item={item}
                      isFeatured={item.mediaId === featuredImageId}
                      isSwapping={swappingItemId === item.id}
                      onDelete={handleDelete}
                      onSetFeatured={handleSetFeatured}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      <Dialog open={uploadModalOpen} onOpenChange={setUploadModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Image to Gallery</DialogTitle>
            <DialogDescription>
              Upload a new image to add to this attraction's gallery
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <ImageUploadDropzone
              value={newMediaId}
              onChange={setNewMediaId}
              label="Gallery Image"
              required
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUploadSubmit} disabled={!newMediaId || addMutation.isPending}>
              {addMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add to Gallery
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmId !== null} onOpenChange={() => setDeleteConfirmId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Gallery Image</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this image from the gallery? This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
